import { logoutAction } from 'actions/authorization';
import { api } from 'api';
import { getGretchOptions } from 'config/gretchen';
import { store } from 'config/store';
import { ErrorsCodeEnum } from 'constants/apiErrorCodes';
import { gretch, GretchOptions } from 'gretchen';
import { RootState } from 'reducers/rootReducer';
import { APIError, APIRequestObj, Authorization } from 'types/api';
import { accountManager } from 'utils/accountManager';
import { showErrorToast } from 'utils/toast';

export const APIErrorNotify = (message?: string) =>
  showErrorToast('Errore', message ?? 'Abbiamo riscontrato un errore inaspettato, riprova tra qualche istante');

export const getAuthorizationInfo = (): Authorization => {
  const state: RootState = store.getState();
  const assignment = state.assignmentReducer?.assignment;
  return [assignment?.pid, assignment?.interventoID];
};

const getAPIInstance = <T>(APIObj: APIRequestObj) => {
  const { url, requestConfig, additionalOpts } = APIObj;
  const opts: GretchOptions = { ...getGretchOptions(requestConfig), ...additionalOpts };
  return () => gretch<T, APIError>(url, opts);
};

export const APIManager = async <T>(APIObj: APIRequestObj): Promise<T | undefined> => {
  try {
    const instance = getAPIInstance<T>(APIObj);
    const { response, status } = await instance().flush();
    const { alreadyTriedToRefresh, justResponse } = APIObj.requestConfig;

    // Handle successful responses
    if (response.ok) {
      if (justResponse) {
        // Return response object directly for justResponse cases
        return (response as unknown) as T;
      }

      try {
        const data = await response.json();
        return data as T;
      } catch (jsonError) {
        // If JSON parsing fails on successful response, return undefined
        APIErrorNotify('Si è verificato un problema temporaneo. Riprova tra qualche istante.');
        return undefined;
      }
    }

    // Handle error responses
    let error: APIError | null = null;
    try {
      error = (await response.json()) as APIError;
    } catch (jsonError) {
      // If we can't parse error JSON, create a generic error
      error = {
        code: status,
        message: `Errore HTTP ${status}`
      };
    }

    // Handle unauthorized cases with token refresh
    if (!alreadyTriedToRefresh && (status === 401 || error?.code === ErrorsCodeEnum.NOT_AUTHENTICADED)) {
      const refreshToken = accountManager.cookie.refreshToken.get();

      if (refreshToken) {
        try {
          const newAccessToken = await api.authorization.refreshAccessToken(refreshToken);

          if (newAccessToken) {
            accountManager.updateAccessTokenInWholeApplication(newAccessToken);

            // Create new API object with updated token
            const newAPIObj: APIRequestObj = {
              ...APIObj,
              requestConfig: {
                ...APIObj.requestConfig,
                headerConfig: {
                  ...APIObj.requestConfig.headerConfig,
                  accessToken: newAccessToken
                },
                alreadyTriedToRefresh: true
              }
            };

            return APIManager(newAPIObj);
          }
        } catch (refreshError) {
          // Refresh token failed, proceed to logout
          store.dispatch(logoutAction());
          return undefined;
        }
      }

      // Failed to refresh token, logout user (avoid to notify)
      // APIErrorNotify(error?.message || 'Sessione scaduta');
      store.dispatch(logoutAction());
      return undefined;
    }

    // Handle other API errors
    APIErrorNotify(error?.message);
    return undefined;
  } catch (networkError) {
    // Handle network errors or other unexpected errors
    APIErrorNotify('Errore di connessione. Verifica la tua connessione internet.');
    return undefined;
  }
};
